import { useRef, useEffect } from 'react';

const remedies = [
  {
    icon: 'ri-heart-pulse-line',
    name: 'Diabetes Support',
    description: 'Coconut flour, black seed oil and specialised blends help regulate blood sugar naturally.',
    color: 'bg-brand-olive/10 text-brand-olive',
  },
  {
    icon: 'ri-capsule-line',
    name: 'Digestion Improvement',
    description: 'Hand-ground spices and digestive seed mixes promote healthy gut function and comfort.',
    color: 'bg-brand-gold/10 text-brand-gold',
  },
  {
    icon: 'ri-leaf-line',
    name: 'Piles Relief',
    description: 'Traditional herbal oil blends and seed mixtures with proven soothing properties.',
    color: 'bg-brand-beige text-brand-brown',
  },
  {
    icon: 'ri-shield-check-line',
    name: 'General Wellness',
    description: 'Daily use of pure organic products builds immunity, energy and lasting vitality.',
    color: 'bg-brand-brown/10 text-brand-brown-dark',
  },
];

const HealthRemedies = () => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.querySelectorAll('.scroll-animate').forEach((child) => {
              child.classList.add('visible');
            });
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.08 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-20 md:py-28 bg-brand-olive/10 px-4 md:px-8" ref={ref}>
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left: Image */}
          <div className="scroll-animate">
            <div className="relative rounded-2xl overflow-hidden h-[420px] md:h-[520px] w-full">
              <img
                src="https://readdy.ai/api/search-image?query=woman%20preparing%20natural%20herbal%20remedy%20with%20mortar%20pestle%20organic%20herbs%20roots%20seeds%20traditional%20handmade%20process%20warm%20golden%20kitchen%20light%20natural%20peaceful%20wellness%20ayurvedic%20photography%20beautiful%20calm%20serene&width=700&height=900&seq=pf3001&orientation=portrait"
                alt="Natural health remedies preparation"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-olive-dark/30 to-transparent"></div>
              <div className="absolute bottom-6 left-6 bg-white/90 backdrop-blur-sm rounded-xl px-5 py-4">
                <p className="font-serif text-brand-brown-dark text-sm font-semibold">
                  Traditional Remedies
                </p>
                <p className="font-sans text-brand-olive text-xs mt-0.5">
                  Trusted for generations
                </p>
              </div>
            </div>
          </div>

          {/* Right: Content */}
          <div>
            <div className="scroll-animate mb-8">
              <span className="inline-block font-sans text-xs tracking-[0.25em] uppercase text-brand-olive font-medium mb-3">
                (Natural Healing Solutions)
              </span>
              <h2 className="font-serif text-3xl md:text-4xl text-brand-brown-dark font-bold leading-tight mb-4">
                Traditional Remedies<br />
                for Modern Life
              </h2>
              <p className="font-sans text-brand-brown/70 text-sm md:text-base leading-relaxed">
                Our products carry the wisdom of generations — natural, time-tested approaches to common health challenges.
              </p>
            </div>

            <div className="space-y-4">
              {remedies.map((remedy, index) => (
                <div
                  key={remedy.name}
                  className={`scroll-animate scroll-animate-delay-${index + 1} flex items-start gap-4 bg-white rounded-xl px-5 py-4`}
                >
                  <div className={`w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0 ${remedy.color}`}>
                    <i className={`${remedy.icon} text-lg`}></i>
                  </div>
                  <div>
                    <h3 className="font-serif text-brand-brown-dark text-base font-semibold mb-1">
                      {remedy.name}
                    </h3>
                    <p className="font-sans text-brand-brown/70 text-sm leading-relaxed">
                      {remedy.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 scroll-animate">
              <a
                href="https://wa.me/923445299984?text=I'd%20like%20to%20know%20more%20about%20your%20natural%20remedies"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-brand-olive hover:text-brand-olive-dark font-sans text-sm font-medium transition-colors duration-200 cursor-pointer"
              >
                Discover Our Healing Range
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HealthRemedies;
